import React, { FC, useState, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState } from "@/src/State/store";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { FiFilter } from "react-icons/fi";
import { Label } from "../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";

interface Channel {
  channel_id: number;
  channel_name: string;
}

type MainFilterOption = "None" | "Channel" | "Status" | "ApprovalStatus" | "StartedAt";
type SubFilterOption = string;

const OperatorCampaignListDropdown: FC<{
  setFilterData: React.Dispatch<
    React.SetStateAction<{ filter: string; subFilter: string }>
  >;
  filterData: { filter: string; subFilter: string };
  dateList: string[];
}> = ({ setFilterData, filterData, dateList }) => {
  const [selectedMainFilter, setSelectedMainFilter] =
    useState<MainFilterOption>("None");
  const [selectedSubFilter, setSelectedSubFilter] =
    useState<SubFilterOption>("");
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [channelList, setChannelList] = useState<Channel[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [channelDropdownOpen, setChannelDropdownOpen] = useState(false);
  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);

  // Fetch channel list
  useEffect(() => {
    const getChannelList = async () => {
      try {
        const res = await axios.get(`${apiUrlAdvAcc}/GetChannelList`);
        if (res?.data?.channelList) {
          setChannelList(res.data.channelList);
        }
      } catch (err) {
        console.error("Error fetching channel list:", err);
      }
    };
    if (apiUrlAdvAcc) getChannelList();
  }, [apiUrlAdvAcc]);

  // Sync with parent
  useEffect(() => {
    setSelectedMainFilter(filterData.filter as MainFilterOption);
    
    // Parse selected channels if filter is Channel
    if (filterData.filter === "Channel" && filterData.subFilter) {
      setSelectedChannels(filterData.subFilter.split(","));
      setSelectedSubFilter("");
    } else if (filterData.filter === "Status" && filterData.subFilter) {
      // Convert lowercase from API back to title case for display
      const displayValue = filterData.subFilter.charAt(0).toUpperCase() + filterData.subFilter.slice(1);
      setSelectedSubFilter(displayValue);
      setSelectedChannels([]);
    } else if (filterData.filter === "ApprovalStatus" && filterData.subFilter) {
      // Convert lowercase from API back to title case for display
      const displayValue = filterData.subFilter.charAt(0).toUpperCase() + filterData.subFilter.slice(1);
      setSelectedSubFilter(displayValue);
      setSelectedChannels([]);
    } else {
      setSelectedSubFilter(filterData.subFilter);
      setSelectedChannels([]);
    }
  }, [filterData]);

  // Handle main filter change
  const handleMainFilterChange = (filter: MainFilterOption) => {
    setSelectedMainFilter(filter);
    setSelectedSubFilter("");
    setSelectedChannels([]);

    if (filter === "Channel") {
      // Set first channel as default if available
      if (channelList.length > 0) {
        const defaultChannel = channelList[0].channel_name;
        setSelectedChannels([defaultChannel]);
        setFilterData({ filter, subFilter: defaultChannel });
      } else {
        setFilterData({ filter, subFilter: "" });
      }
    } else if (filter === "Status") {
      const defaultStatus = "active";
      setSelectedSubFilter("Active");
      setFilterData({ filter, subFilter: defaultStatus });
    } else if (filter === "ApprovalStatus") {
      const defaultApproval = "approved";
      setSelectedSubFilter("Approved");
      setFilterData({ filter, subFilter: defaultApproval });
    } else if (filter === "StartedAt") {
      // Set first date as default if available
      if (dateList.length > 0) {
        setSelectedSubFilter(dateList[0]);
        setFilterData({ filter, subFilter: dateList[0] });
      } else {
        setFilterData({ filter, subFilter: "" });
      }
    } else {
      setFilterData({ filter: "None", subFilter: "" });
    }

    setIsOpen(true);
  };

  // Handle channel selection in dropdown
  const handleChannelSelect = (channel: string) => {
    let newChannels: string[];
    
    if (selectedChannels.includes(channel)) {
      newChannels = selectedChannels.filter((c) => c !== channel);
    } else {
      newChannels = [...selectedChannels, channel];
    }
    
    setSelectedChannels(newChannels);
    
    // Join selected channels with comma
    const channelValue = newChannels.join(",");
    setFilterData({ filter: "Channel", subFilter: channelValue });
    
    // Keep dropdown open for multi-select
    setChannelDropdownOpen(true);
  };

  // Handle subfilter change for non-Channel filters
  const handleSubFilterChange = (value: SubFilterOption) => {
    setSelectedSubFilter(value);
    
    // Convert Status values to lowercase before sending to API
    let apiValue = value;
    if (selectedMainFilter === "Status") {
      apiValue = value.toLowerCase();
    } else if (selectedMainFilter === "ApprovalStatus") {
      apiValue = value.toLowerCase();
    }
    
    setFilterData({ filter: selectedMainFilter, subFilter: apiValue });
    setIsOpen(true);
  };

  // Get display text for filter button
  const getFilterButtonText = () => {
    if (selectedMainFilter === "None" || !filterData.subFilter) {
      return "Filter";
    }
    
    if (selectedMainFilter === "Channel" && selectedChannels.length > 0) {
      return `${selectedMainFilter}: ${selectedChannels.join(", ")}`;
    }
    
    // For Status, display with proper case
    if (selectedMainFilter === "Status") {
      const displayValue = filterData.subFilter.charAt(0).toUpperCase() + filterData.subFilter.slice(1);
      return `${selectedMainFilter}: ${displayValue}`;
    }
    
    // For ApprovalStatus, display with proper case
    if (selectedMainFilter === "ApprovalStatus") {
      const displayValue = filterData.subFilter.charAt(0).toUpperCase() + filterData.subFilter.slice(1);
      return `Approval Status: ${displayValue}`;
    }
    
    return `${selectedMainFilter}: ${filterData.subFilter}`;
  };

  // Get display text for channel dropdown
  const getChannelDisplayText = () => {
    if (selectedChannels.length === 0) {
      return "Select channels";
    }
    return selectedChannels.join(", ");
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full font-medium text-[14px] py-2 px-3 !mt-0"
        >
          <FiFilter
            style={{ width: "14px", height: "14px" }}
            className="mr-1"
          />
          {getFilterButtonText()}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent 
        className="w-64 p-3 rounded-md shadow-md space-y-4"
        side="left"
        align="start"
        sideOffset={8}
      >
        {/* Main Filter */}
        <div className="flex w-full">
          <div className="basis-1/3 flex justify-center items-center">
            <Label className="text-sm text-[#020617]">Criteria</Label>
          </div>
          <div className="basis-2/3 flex justify-center items-center">
            <Select
              value={selectedMainFilter}
              onValueChange={(value) =>
                handleMainFilterChange(value as MainFilterOption)
              }
            >
              <SelectTrigger className="text-gray-500 w-full h-8">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Channel" className="cursor-pointer">
                  Channel
                </SelectItem>
                <SelectItem value="Status" className="cursor-pointer">
                  Status
                </SelectItem>
                <SelectItem value="ApprovalStatus" className="cursor-pointer">
                  Approval Status
                </SelectItem>
                <SelectItem value="StartedAt" className="cursor-pointer">
                  Started At
                </SelectItem>
                <SelectItem value="None" className="cursor-pointer">
                  None
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Subfilter */}
        {selectedMainFilter !== "None" && (
          <div className="flex w-full">
            <div className="basis-1/3 flex justify-center items-center">
              <Label className="text-sm text-[#020617]">Subfilter</Label>
            </div>
            <div className="basis-2/3 flex justify-center items-center">
              {selectedMainFilter === "Channel" && (
                <Select
                  open={channelDropdownOpen}
                  onOpenChange={setChannelDropdownOpen}
                  value={selectedChannels.length > 0 ? "selected" : ""}
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue>
                      <span className={selectedChannels.length === 0 ? "text-gray-500" : ""}>
                        {getChannelDisplayText()}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {channelList.map((channel) => (
                      <div
                        key={channel.channel_id}
                        className="flex items-center space-x-2 px-2 py-1.5 cursor-pointer hover:bg-gray-100"
                        onClick={(e) => {
                          e.preventDefault();
                          handleChannelSelect(channel.channel_name);
                        }}
                      >
                        <Checkbox
                          id={`channel-${channel.channel_id}`}
                          checked={selectedChannels.includes(channel.channel_name)}
                          onCheckedChange={() => handleChannelSelect(channel.channel_name)}
                        />
                        <label
                          htmlFor={`channel-${channel.channel_id}`}
                          className="text-sm font-medium leading-none cursor-pointer flex-1"
                        >
                          {channel.channel_name}
                        </label>
                      </div>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "Status" && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue placeholder="Select status">
                      <span className="truncate block">
                        {selectedSubFilter || "Select status"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active" className="cursor-pointer">
                      Active
                    </SelectItem>
                    <SelectItem value="Paused" className="cursor-pointer">
                      Paused
                    </SelectItem>
                    <SelectItem value="Completed" className="cursor-pointer">
                      Completed
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "ApprovalStatus" && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue placeholder="Select approval">
                      <span className="truncate block">
                        {selectedSubFilter || "Select approval"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Approved" className="cursor-pointer">
                      Approved
                    </SelectItem>
                    <SelectItem value="Pending" className="cursor-pointer">
                      Pending
                    </SelectItem>
                    <SelectItem value="Rejected" className="cursor-pointer">
                      Rejected
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {selectedMainFilter === "StartedAt" && (
                <Select
                  value={selectedSubFilter}
                  onValueChange={(value) =>
                    handleSubFilterChange(value as SubFilterOption)
                  }
                >
                  <SelectTrigger className="text-gray-500 w-[150px] h-8">
                    <SelectValue placeholder="Select date">
                      <span className="truncate block">
                        {selectedSubFilter || "Select date"}
                      </span>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="max-h-[200px] overflow-y-auto">
                    {dateList.map((date) => (
                      <SelectItem key={date} value={date} className="cursor-pointer">
                        {date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default OperatorCampaignListDropdown;